import styled from '@emotion/styled'
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import {
  Button,
  Collapse,
  Empty,
  Popconfirm,
  Skeleton,
  Tag,
  Typography,
  message,
} from 'antd'
import { FC, useMemo, useState } from 'react'
import { backendUrl } from 'config'
import SearchProductForm from '../SearchProductForm'
import {
  useDeleteProductRecipe,
  useGetProductRecipe,
  useListProductRecipes,
} from 'hooks/useProductRecipe'
import type {
  ProductRecipeItemRecord,
  ProductRecipeRecord,
} from 'services/interface/productRecipe.interface'
import RecipeFormModal from './RecipeFormModal'
import StockImageThumb from '../StockImageThumb'
import {
  displayRefTypeLabel,
  MASTER_STOCK_LABEL,
  SET_TAG_LABEL,
} from './bundleLabels'

const recipeListImageUrl = (recipe: ProductRecipeRecord): string | null => {
  // ประกอบจาก image id + backendUrl ก่อน (เส้นที่ใช้ได้จริง) ไม่มีค่อย fallback image_url
  const imageId = recipe.images?.[0]
  if (imageId) return `${backendUrl}images/${imageId}`
  return recipe.image_url?.trim() || null
}

/** รูปส่วนประกอบในชุดเซ็ต — image id + backendUrl ก่อน, ไม่มีค่อย ref_image_url */
const recipeItemImageUrl = (item: ProductRecipeItemRecord): string | null => {
  if (item.ref_image_id) return `${backendUrl}images/${item.ref_image_id}`
  return item.ref_image_url?.trim() || null
}

const itemTagColor = (item: ProductRecipeItemRecord): string => {
  if (item.ref_type === 'product_recipe') return 'magenta'
  if (
    item.ref_stock_type_label === 'สต็อกทั่วไป' ||
    item.ref_stock_type_label === 'มาสเตอร์สินค้า' ||
    item.ref_stock_type_label === MASTER_STOCK_LABEL
  )
    return 'blue'
  if (item.ref_stock_type_label === 'สต็อก SKU') return 'purple'
  return 'default'
}

const formatItemQty = (quantity: number) =>
  Number(quantity).toLocaleString('th-TH', {
    maximumFractionDigits: 2,
  })

/** จำนวนต่อชุด — แยกสัญลักษณ์ × กับตัวเลข อ่านง่าย */
const ItemQuantityBadge: FC<{
  quantity: number
  size?: 'md' | 'lg'
}> = ({ quantity, size = 'md' }) => {
  const n = Number(quantity)
  return (
    <QtyPill $size={size} aria-label={`จำนวน ${formatItemQty(n)}`}>
      <QtyMark $size={size}>×</QtyMark>
      <QtyNum $size={size}>{formatItemQty(n)}</QtyNum>
    </QtyPill>
  )
}

const RecipeInventoryPanel: FC = () => {
  const [search_input, set_search_input] = useState('')
  const [create_open, set_create_open] = useState(false)
  const [edit_id, set_edit_id] = useState<string | null>(null)
  const [expanded_recipe_id, set_expanded_recipe_id] = useState<string | null>(null)

  const listQuery = useListProductRecipes({
    search: search_input.trim() || undefined,
    page: 1,
    limit: 100,
  })

  const editDetailQuery = useGetProductRecipe(edit_id, !!edit_id)
  const expandDetailQuery = useGetProductRecipe(
    expanded_recipe_id,
    !!expanded_recipe_id && expanded_recipe_id !== edit_id
  )

  const { mutate: deleteMutate, isPending: deleting } = useDeleteProductRecipe()

  const recipes = useMemo(
    () => listQuery.data?.data ?? [],
    [listQuery.data?.data]
  )

  const editing_recipe = useMemo(() => {
    if (!edit_id || !editDetailQuery.data) return null
    return {
      ...editDetailQuery.data.recipe,
      items: editDetailQuery.data.items,
    }
  }, [edit_id, editDetailQuery.data])

  const expanded_items = useMemo(() => {
    if (!expanded_recipe_id) return []
    if (expanded_recipe_id === edit_id && editDetailQuery.data) {
      return editDetailQuery.data.items
    }
    return expandDetailQuery.data?.items ?? []
  }, [expanded_recipe_id, edit_id, editDetailQuery.data, expandDetailQuery.data])

  const openEdit = (record: ProductRecipeRecord) => {
    set_edit_id(record.id)
  }

  const collapseItems = recipes.map((recipe) => ({
    key: recipe.id,
    label: (
      <CollapseLabelRow>
        <ListThumbBox>
          <StockImageThumb
            src={recipeListImageUrl(recipe)}
            alt={recipe.name}
          />
        </ListThumbBox>
        <CollapseLabel>
          <Typography.Text strong>{recipe.name}</Typography.Text>
          <TagRow>
            <Tag color="purple">{SET_TAG_LABEL}</Tag>
            <Tag color={recipe.is_active ? 'green' : 'default'}>
              {recipe.is_active ? 'เปิดใช้งาน' : 'ปิด'}
            </Tag>
            <Tag>{recipe.item_count ?? 0} ส่วนประกอบ</Tag>
          </TagRow>
          {(recipe.items_summary?.length ?? 0) > 0 && (
            <SummaryBlock>
              <SummaryChips>
                {recipe.items_summary!.map((s, i) => (
                  <SummaryChip key={i}>
                    <SummaryChipName title={s.ref_label}>
                      {s.ref_label}
                    </SummaryChipName>
                    <ItemQuantityBadge quantity={s.quantity} />
                  </SummaryChip>
                ))}
              </SummaryChips>
              {(recipe.item_count ?? 0) > (recipe.items_summary?.length ?? 0) ? (
                <SummaryMore>
                  +{(recipe.item_count ?? 0) - (recipe.items_summary?.length ?? 0)}{' '}
                  รายการ
                </SummaryMore>
              ) : null}
            </SummaryBlock>
          )}
        </CollapseLabel>
      </CollapseLabelRow>
    ),
    extra: (
      <RowActions onClick={(e) => e.stopPropagation()}>
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          loading={edit_id === recipe.id && editDetailQuery.isFetching}
          onClick={() => openEdit(recipe)}
        />
        <Popconfirm
          title="ลบชุดเซ็ตนี้?"
          okText="ลบ"
          cancelText="ยกเลิก"
          onConfirm={() =>
            deleteMutate(recipe.id, {
              onSuccess: () => {
                message.success('ลบชุดเซ็ตแล้ว')
                if (expanded_recipe_id === recipe.id) set_expanded_recipe_id(null)
              },
              onError: (err) =>
                message.error(err.message || 'ลบไม่สำเร็จ'),
            })
          }
        >
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            loading={deleting}
          />
        </Popconfirm>
      </RowActions>
    ),
    children:
      expanded_recipe_id === recipe.id ? (
        expandDetailQuery.isFetching && !expandDetailQuery.data ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : expanded_items.length === 0 ? (
          <Typography.Text type="secondary">ไม่มีส่วนประกอบ</Typography.Text>
        ) : (
          <ItemGrid>
            {expanded_items.map((item) => (
              <ComponentCard key={item.id}>
                <CardThumb>
                  <StockImageThumb
                    src={recipeItemImageUrl(item)}
                    alt={item.ref_label || item.ref_name || ''}
                  />
                </CardThumb>
                <CardBody>
                  <CardTitleRow>
                    <CardTitle title={item.ref_label || item.ref_name || item.ref_id}>
                      {item.ref_label || item.ref_name || item.ref_id}
                    </CardTitle>
                    <ItemQuantityBadge quantity={item.quantity} />
                  </CardTitleRow>
                  {item.ref_balance != null ? (
                    <CardMeta>คงเหลือ {item.ref_balance.toLocaleString()}</CardMeta>
                  ) : null}
                  <CardFooter>
                    <Tag
                      color={itemTagColor(item)}
                      style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}
                    >
                      {displayRefTypeLabel(
                        item.ref_stock_type_label,
                        item.ref_type
                      )}
                    </Tag>
                  </CardFooter>
                </CardBody>
              </ComponentCard>
            ))}
          </ItemGrid>
        )
      ) : null,
  }))

  return (
    <Wrap>
      <Toolbar>
        <ToolbarRightCluster>
          <SearchSlot>
            <SearchProductForm
              compact
              placeholder="ค้นหา"
              searchInput={search_input}
              setSearchInput={set_search_input}
            />
          </SearchSlot>
          <Button
            icon={<ReloadOutlined />}
            loading={listQuery.isFetching}
            onClick={() => listQuery.refetch()}
          >
            รีโหลด
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => set_create_open(true)}
          >
            เพิ่มชุดเซ็ต
          </Button>
        </ToolbarRightCluster>
      </Toolbar>

      <RecipeFormModal open={create_open} onClose={() => set_create_open(false)} />

      <RecipeFormModal
        open={!!edit_id}
        editing={editing_recipe}
        loading_detail={!!edit_id && editDetailQuery.isFetching}
        onClose={() => set_edit_id(null)}
      />

      {listQuery.isPending || (listQuery.isFetching && !listQuery.data) ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : recipes.length === 0 ? (
        <EmptyBox>
          <Empty
            description={
              search_input.trim()
                ? 'ไม่พบชุดเซ็ตที่ตรงกับคำค้นหา'
                : 'ยังไม่มีชุดเซ็ต — กดเพิ่มชุดเซ็ตเพื่อเริ่มต้น'
            }
          />
        </EmptyBox>
      ) : (
        <Collapse
          accordion
          items={collapseItems}
          activeKey={expanded_recipe_id ?? undefined}
          onChange={(key) => {
            const k = Array.isArray(key) ? key[0] : key
            set_expanded_recipe_id(k ? String(k) : null)
          }}
        />
      )}
    </Wrap>
  )
}

export default RecipeInventoryPanel

const Wrap = styled.div`
  padding-top: 4px;
`

const Toolbar = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 12px;
  overflow-x: auto;
`;

const ToolbarRightCluster = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const SearchSlot = styled.div`
  flex: 0 0 auto;
  & > div {
    margin-bottom: 0;
    width: auto;
  }
`;

const EmptyBox = styled.div`
  padding: 40px;
  background: #fafafa;
  border-radius: 8px;
`

const CollapseLabelRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  padding-right: 8px;
`

const ListThumbBox = styled.div`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
`

const CollapseLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const SummaryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 2px;
`

const SummaryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const SummaryChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 4px 8px 4px 10px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
`

const SummaryChipName = styled.span`
  font-size: 12px;
  color: #434343;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`

const SummaryMore = styled.span`
  font-size: 12px;
  color: #8c8c8c;
`

const RowActions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 4px;
`

const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 6px;
  padding: 2px 0;
`

const ComponentCard = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  overflow: hidden;
  min-width: 0;
  min-height: 52px;
`

const CardThumb = styled.div`
  width: 44px;
  min-height: 44px;
  align-self: stretch;
  flex-shrink: 0;
  background: #fafafa;
  border-right: 1px solid #f0f0f0;
`

const CardBody = styled.div`
  padding: 5px 8px 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  flex: 1;
`

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
`

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
  color: #141414;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`

const CardMeta = styled.div`
  font-size: 10px;
  color: #8c8c8c;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-top: 1px;
`

const QtyPill = styled.span<{ $size: 'md' | 'lg' }>`
  display: inline-flex;
  align-items: baseline;
  flex-shrink: 0;
  gap: 2px;
  padding: ${({ $size }) => ($size === 'lg' ? '2px 8px' : '1px 6px')};
  border-radius: 6px;
  background: #1677ff;
  color: #fff;
  line-height: 1.15;
`

const QtyMark = styled.span<{ $size: 'md' | 'lg' }>`
  font-size: ${({ $size }) => ($size === 'lg' ? '11px' : '10px')};
  font-weight: 600;
  opacity: 0.9;
`

const QtyNum = styled.span<{ $size: 'md' | 'lg' }>`
  font-size: ${({ $size }) => ($size === 'lg' ? '13px' : '12px')};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`
